import React from 'react';
import { useTranslation } from '../utils/i18n';
import IkiSymbol from '../components/IkiSymbol';

function VoiceIntegrity() {
  const content = {
    title: "Voice Integrity",
    subtitle: "Privacy Guaranteed",
    intro: "Your voice is an extension of your consciousness.\nWhen you choose to record it, you create a private tool to dialogue with yourself — built with respect, safety, and complete confidentiality.",
    sections: [
      {
        title: "Our Promise",
        content: "Your recordings are encrypted and stored securely.\nThey are never used for external training or analysis.\nYour voice remains yours — always."
      }
    ],
    lastUpdated: "Last updated: November 5, 2025"
  };

  // Old multilingual content removed - English only
  const oldContent = {
    en: {
      title: "Voice Integrity Policy",
      intro: "Your voice is an extension of your consciousness. When you record it, you create a private tool to speak with yourself with respect and safety.",
      sections: [
        {
          title: "Our promise",
          content: "Your voice is an extension of your consciousness. When you record it, you create a private tool to speak with yourself with respect and safety. HYPSEYA uses your sample only to generate meditations and affirmations in your own voice."
        },
        {
          title: "How we protect your voice",
          content: "Your sample is encrypted and stored only in your profile. It is never sold, shared or used to train external AI systems. You can delete recordings at any time in Settings.",
          points: [
            "All voice samples are encrypted with AES-256 at the moment of upload.",
            "Files are stored in private storage containers dedicated to your user_id.",
            "Only you have access to your samples through your authenticated account.",
            "Data in transit is protected with TLS 1.3."
          ]
        },
        {
          title: "Purpose",
          content: "The purpose is clear and transparent: to let you hear yourself, recognize yourself and reprogram in coherence with your own energy. Your voice is your code and we treat it as sacred."
        },
        {
          title: "How we protect your voice",
          points: [
            "All voice samples are encrypted with AES-256 at the moment of upload.",
            "Files are stored in private storage containers dedicated to your user_id.",
            "Only you have access to your samples through your authenticated account.",
            "We implement strict access controls and regular security audits.",
            "Data in transit is protected with TLS 1.3."
          ]
        },
        {
          title: "Your rights",
          points: [
            "You can view all your saved voice samples at any time.",
            "You can permanently delete your voice samples via Settings > Privacy > Manage my voice samples.",
            "When you delete a sample, it is permanently and irreversibly removed from our systems within 24 hours.",
            "If you delete your account, all voice samples are automatically erased."
          ]
        },
        {
          title: "Disclaimer",
          content: "HYPSEYA is an educational platform. It does not provide substances and does not replace medical advice."
        }
      ],
      lastUpdated: "Last updated: November 5, 2025"
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #F7F3EC 0%, #EFE7DD 50%, #B8D3CF 100%)',
      padding: '40px 20px'
    }}>
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        background: 'rgba(255, 255, 255, 0.95)',
        borderRadius: '20px',
        padding: '48px',
        boxShadow: '0 8px 32px rgba(30, 107, 104, 0.12)'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '48px' }}>
          <h1 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: '2.5rem',
            color: '#1E6B68',
            marginBottom: '16px'
          }}>
            {content.title}
          </h1>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 24px',
            background: 'rgba(30, 107, 104, 0.1)',
            borderRadius: '24px',
            marginBottom: '24px'
          }}>
            <IkiSymbol name="lock" size="sm" />
            <span style={{ color: '#1E6B68', fontWeight: '600' }}>
              {content.subtitle}
            </span>
          </div>
        </div>

        {/* Intro */}
        <p style={{
          fontSize: '1.1rem',
          lineHeight: '1.8',
          color: '#2D8B87',
          marginBottom: '40px',
          textAlign: 'center',
          fontStyle: 'italic',
          whiteSpace: 'pre-line'
        }}>
          {content.intro}
        </p>

        {/* Sections */}
        {content.sections.map((section, idx) => (
          <div key={idx} style={{ marginBottom: '40px' }}>
            <h2 style={{
              fontFamily: "'Playfair Display', serif",
              fontSize: '1.5rem',
              color: '#1E6B68',
              marginBottom: '16px',
              borderLeft: '4px solid #D9CBA3',
              paddingLeft: '16px'
            }}>
              {section.title}
            </h2>
            
            {section.content && (
              <p style={{
                fontSize: '1rem',
                lineHeight: '1.7',
                color: '#2D8B87',
                marginBottom: '12px',
                whiteSpace: 'pre-line'
              }}>
                {section.content}
              </p>
            )}

            {section.points && (
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {section.points.map((point, pointIdx) => (
                  <li key={pointIdx} style={{
                    fontSize: '1rem',
                    lineHeight: '1.7',
                    color: '#666',
                    marginBottom: '12px',
                    paddingLeft: '32px',
                    position: 'relative'
                  }}>
                    <span style={{
                      position: 'absolute',
                      left: '0',
                      color: '#1E6B68',
                      fontWeight: 'bold'
                    }}>•</span>
                    {point}
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}

        {/* Footer */}
        <div style={{
          marginTop: '48px',
          paddingTop: '24px',
          borderTop: '2px solid rgba(30, 107, 104, 0.1)',
          textAlign: 'center'
        }}>
          <p style={{
            fontSize: '0.9rem',
            color: '#999',
            marginBottom: '16px'
          }}>
            {content.lastUpdated}
          </p>
          <a
            href="/voice-lab"
            style={{
              display: 'inline-block',
              padding: '12px 32px',
              background: 'linear-gradient(135deg, #1E6B68 0%, #2D8B87 100%)',
              color: '#fff',
              textDecoration: 'none',
              borderRadius: '24px',
              fontSize: '1rem',
              transition: 'transform 0.2s ease',
              boxShadow: '0 4px 12px rgba(30, 107, 104, 0.3)'
            }}
            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
          >
            Back to Voice Lab
          </a>
        </div>
      </div>
    </div>
  );
}

export default VoiceIntegrity;
